import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, map, take, takeUntil, debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import {
  BASE_PERMISSIONS,
  BaseButtonSize,
  BaseCoreState,
  BaseCustomerInquiryFilters,
  baseInquiryActions,
  baseInquirySelectors,
  BaseQueryParamService,
  BaseRolesService,
  CustomerInquiryListItemResponse,
  QualificationShortInfoResponse,
  UserShortInfoResponse,
  BaseInquiriesPayloadInterface,
  BaseListPagination,
  CustomerDepartmentFilterInfoResponse,
  CustomerLocationFilterInfoResponse,
  BaseInquiryType,
} from 'base';
import { BaseCustomerInquiryListItemType } from '../../_interfaces/base-inquiry.type';

@Component({
  selector: 'base-customer-inquiry-list',
  templateUrl: './base-customer-inquiry-list.component.html',
})
export class BaseCustomerInquiryListComponent implements OnInit, OnDestroy {
  qualifications: QualificationShortInfoResponse[] = [];
  departments: CustomerDepartmentFilterInfoResponse[] = [];
  locations: CustomerLocationFilterInfoResponse[] = [];
  createdByCustomers: UserShortInfoResponse[] = [];
  inquiries: BaseCustomerInquiryListItemType[];
  inquiriesLoaded = false;
  buttonSizes = BaseButtonSize;
  copiedInquiry: BaseInquiryType;

  filters: { [key in any]: any } = {
    [BaseCustomerInquiryFilters.status]: undefined,
    [BaseCustomerInquiryFilters.department]: [],
    [BaseCustomerInquiryFilters.location]: [],
    [BaseCustomerInquiryFilters.mainQualification]: [],
    [BaseCustomerInquiryFilters.createdBy]: undefined,
    [BaseCustomerInquiryFilters.search]: undefined,
  };
  page = BaseListPagination.page;
  size = BaseListPagination.size;
  itemsCount = 0;
  canPublishInquiry = false;
  departmentColumnLabel: string;

  private destroy$ = new Subject();

  constructor(
    private store: Store<BaseCoreState>,
    private queryParamsService: BaseQueryParamService,
    private rolesService: BaseRolesService,
    private translateService: TranslateService,
    private activatedRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.setDepartmentColumnLabel();
    this.canPublishInquiry = this.rolesService.hasCustomerFeaturePermission(BASE_PERMISSIONS.FEATURE_PUBLISH_INQUIRY);

    this.getInquiries();
    this.getInquiryFilters();
    this.fetchFilteredInquiries();

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.setDepartmentColumnLabel();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  private fetchFilteredInquiries() {
    this.queryParamsService.filterSubject.pipe(takeUntil(this.destroy$), debounceTime(50)).subscribe((value) => {
      this.queryParamsService.redirectWithQueryParams(
        value,
        {
          status: 'id',
          search: 'value',
          createdBy: 'id',
        },
        () => this.fetchInquiries()
      );
    });
  }

  private getInquiries(): void {
    this.store
      .select(baseInquirySelectors.getInquiries)
      .pipe(
        takeUntil(this.destroy$),
        filter((state) => !!state.ok),
        map((response) => response.data)
      )
      .subscribe((state) => {
        this.inquiries = state.data;
        this.itemsCount = state.meta?.page.itemsCount as number;
        this.size = state.meta?.page.size as number;
        this.page = state.meta?.page.current as number;
        this.inquiriesLoaded = true;
      });
  }

  private getInquiryFilters() {
    this.store
      .select(baseInquirySelectors.getInquiries)
      .pipe(
        filter((state) => !!state.ok),
        map((state) => state.data?.filters),
        take(1)
      )
      .subscribe((filters) => {
        this.qualifications = filters?.mainQualifications;
        this.departments = filters?.departments;
        this.locations = filters?.customerLocations;
        this.createdByCustomers = filters?.createdByList;
      });
  }

  private fetchInquiries(): void {
    this.store.dispatch(
      baseInquiryActions.getInquiries({
        payload: this.activatedRoute.snapshot.queryParams as BaseInquiriesPayloadInterface,
      })
    );
  }

  private setDepartmentColumnLabel(): void {
    const department = this.translateService.instant('department.singular');
    const location = this.translateService.instant('location.singular');
    this.departmentColumnLabel = `${department} | ${location}`;
  }

  trackByCustomerInquiry(_: number, item: CustomerInquiryListItemResponse) {
    return item.id;
  }
}
